import { useState } from 'react';
import {
  useAiConversations,
  useDeleteAiConversation,
  useRenameAiConversation,
} from '@/api/ai-conversations/ai-conversations.hooks';
import { aiConversationsApi } from '@/api';
import type { AssistantConversationSummary } from '@/api/ai/ai.types';
import type { AssistantConversationMessage } from '@/api/ai/ai.types';

interface AssistantHistorySidebarProps {
  /** Id of the currently visible conversation, or `null` for the blank state. */
  activeConversationId: string | null;
  /** Asks for a brand-new conversation (clears the chat locally). */
  onNewConversation: () => void;
  /** Loads a stored conversation into the chat. */
  onSelectConversation: (
    id: string,
    messages: AssistantConversationMessage[],
  ) => void;
  /**
   * Called after deleting the currently active conversation — the panel
   * resets back to a blank chat.
   */
  onActiveDeleted: () => void;
}

/**
 * Left-side history rail inside the assistant slide-over. Lists the caller's
 * persisted conversations (`GET /v1/ai/conversations`, sorted by
 * `lastMessageAt`), with row-level actions to rename and delete and a
 * top-of-rail "Neuer Chat" button.
 *
 * Selecting a row fetches `GET /v1/ai/conversations/:id` and hands the
 * hydrated messages to the chat via {@link AssistantHistorySidebarProps.onSelectConversation}.
 */
export function AssistantHistorySidebar({
  activeConversationId,
  onNewConversation,
  onSelectConversation,
  onActiveDeleted,
}: AssistantHistorySidebarProps) {
  const { data: conversations, isLoading } = useAiConversations();
  const renameMutation = useRenameAiConversation();
  const deleteMutation = useDeleteAiConversation();

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [loadingId, setLoadingId] = useState<string | null>(null);

  // If the conversation being renamed disappears from the list (deleted from
  // another tab, server-side cleanup, …), drop the rename UI on the next
  // render. Setting state conditionally during render is the React-canonical
  // alternative to a syncing useEffect.
  if (
    renamingId != null &&
    conversations !== undefined &&
    !conversations.some((c) => c.id === renamingId)
  ) {
    setRenamingId(null);
    setRenameDraft('');
  }

  const handleSelect = async (id: string) => {
    if (id === activeConversationId || loadingId) return;
    setLoadingId(id);
    try {
      const detail = await aiConversationsApi.get(id).then((r) => r.data);
      onSelectConversation(id, detail.messages);
    } finally {
      setLoadingId(null);
    }
  };

  const handleStartRename = (conv: AssistantConversationSummary) => {
    setRenamingId(conv.id);
    setRenameDraft(conv.title ?? '');
  };

  const handleSubmitRename = async () => {
    if (renamingId == null) return;
    const next = renameDraft.trim() || null;
    await renameMutation.mutateAsync({ id: renamingId, title: next });
    setRenamingId(null);
    setRenameDraft('');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Diesen Chat dauerhaft löschen?')) return;
    await deleteMutation.mutateAsync({ id });
    if (id === activeConversationId) onActiveDeleted();
  };

  return (
    <aside className="eos-asst-history" aria-label="Chatverlauf">
      <div className="eos-asst-history-head">
        <button
          type="button"
          className="eos-asst-history-new"
          onClick={onNewConversation}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 3.5v9M3.5 8h9"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          Neuer Chat
        </button>
      </div>
      <div className="eos-asst-history-list ds-scroll">
        {isLoading && (
          <div className="eos-asst-history-empty">Lade Verlauf…</div>
        )}
        {!isLoading && (!conversations || conversations.length === 0) && (
          <div className="eos-asst-history-empty">
            Noch keine Chats. Stell deine erste Frage rechts.
          </div>
        )}
        {conversations?.map((conv) => {
          const isActive = conv.id === activeConversationId;
          const isRenaming = renamingId === conv.id;
          const label = conv.title?.trim() || 'Ohne Titel';
          return (
            <div
              key={conv.id}
              className={`eos-asst-history-row${
                isActive ? ' is-active' : ''
              }${loadingId === conv.id ? ' is-loading' : ''}`}
            >
              {isRenaming ? (
                <input
                  type="text"
                  className="eos-asst-history-rename"
                  value={renameDraft}
                  autoFocus
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onBlur={handleSubmitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSubmitRename();
                    } else if (e.key === 'Escape') {
                      setRenamingId(null);
                      setRenameDraft('');
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="eos-asst-history-row-main"
                  title={label}
                  onClick={() => handleSelect(conv.id)}
                  onDoubleClick={() => handleStartRename(conv)}
                >
                  <span className="eos-asst-history-row-title">{label}</span>
                  <span className="eos-asst-history-row-date">
                    {formatRelative(conv.lastMessageAt)}
                  </span>
                </button>
              )}
              {!isRenaming && (
                <div className="eos-asst-history-row-actions">
                  <button
                    type="button"
                    className="eos-asst-history-row-btn"
                    aria-label="Umbenennen"
                    title="Umbenennen"
                    onClick={() => handleStartRename(conv)}
                  >
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <path
                        d="M3 13l1.5-.4 7-7-1.1-1.1-7 7L3 13zM11.6 3.4l1 1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="eos-asst-history-row-btn is-danger"
                    aria-label="Löschen"
                    title="Löschen"
                    onClick={() => handleDelete(conv.id)}
                  >
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <path
                        d="M4 5h8M6 5V3.5h4V5M5 5l.5 8h5l.5-8M7 7.5v4M9 7.5v4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/**
 * Renders an ISO date as a compact German "Heute / Gestern / DD.MM." label.
 * Older items fall back to `DD.MM.YY` so the column stays narrow.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const oneDay = 86_400_000;
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startOfYesterday = startOfToday - oneDay;
  if (then.getTime() >= startOfToday) {
    return then.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (then.getTime() >= startOfYesterday) return 'Gestern';
  if (diffMs < 7 * oneDay) {
    return then.toLocaleDateString('de-DE', { weekday: 'short' });
  }
  if (then.getFullYear() === now.getFullYear()) {
    return then.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  }
  return then.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}
